import { ref } from 'vue'
import axios from 'axios'
import { sendGraphQLQuery } from '@/services/gql-util'
import { ReportFilters, ReportDataPoint } from '@/types/Reports'
import { toast } from 'vue-sonner'

export default function useReports() {
	const uploadData = ref<ReportDataPoint[]>([])
	const viewData = ref<ReportDataPoint[]>([])
	const bandwidthData = ref<ReportDataPoint[]>([])
	const memberData = ref<ReportDataPoint[]>([])
	const loading = ref(false)
	const error = ref<string | null>(null)

	const getUploadReport = async (vhostId: string, filters: ReportFilters = {}): Promise<void> => {
		loading.value = true
		error.value = null

		try {
			const { data } = await axios.post('/services/json', {
				method: 'vhost.getFileUploads',
				vhostId: [vhostId],
				startDate: filters.startDate || '2024-01-01',
				endDate: filters.endDate || new Date().toISOString().split('T')[0],
				summarizeBy: filters.summarizeBy || 1,
				prependIntervalName: 0,
				runningTotal: 0,
			})

			if (data.status) {
				uploadData.value = (data.result.data || []).map((record: any) => ({
					date: record.RealDate,
					value: record.NumUploads ?? 0,
					label: new Date(record.RealDate).toLocaleString('default', { month: 'short' }),
				}))
			}
		} catch (err: any) {
			console.error('Error fetching upload report:', err)
			error.value = err.message || 'Failed to fetch upload report'
			toast('Error', { description: err.message })
		} finally {
			loading.value = false
		}
	}

	const getViewReport = async (vhostId: string, filters: ReportFilters = {}): Promise<void> => {
		loading.value = true
		error.value = null

		try {
			const { data } = await axios.post('/services/json', {
				method: 'vhost.getFileViews',
				vhostId: [vhostId],
				startDate: filters.startDate || '2024-01-01',
				endDate: filters.endDate || new Date().toISOString().split('T')[0],
				summarizeBy: filters.summarizeBy || 1,
				prependIntervalName: 0,
			})

			if (data.status) {
				viewData.value = (data.result.data || []).map((record: any) => ({
					date: record.RealDate,
					value: record.NumViews ?? 0,
					label: new Date(record.RealDate).toLocaleString('default', { month: 'short' }),
				}))
			}
		} catch (err: any) {
			console.error('Error fetching view report:', err)
			error.value = err.message || 'Failed to fetch view report'
		} finally {
			loading.value = false
		}
	}

	const getBandwidthReport = async (vhostId: string, filters: ReportFilters = {}): Promise<void> => {
		loading.value = true
		error.value = null

		try {
			const { data } = await axios.post('/services/json', {
				method: 'vhost.getBandwidthUsage',
				vhostId: [vhostId],
				startDate: filters.startDate || '2024-01-01',
				endDate: filters.endDate || new Date().toISOString().split('T')[0],
				summarizeBy: filters.summarizeBy || 1,
			})

			if (data.status) {
				bandwidthData.value = (data.result.data || []).map((record: any) => ({
					date: record.RealDate,
					value: record.Bytes ?? 0,
					label: new Date(record.RealDate).toLocaleString('default', { month: 'short' }),
				}))
			}
		} catch (err: any) {
			console.error('Error fetching bandwidth report:', err)
			error.value = err.message || 'Failed to fetch bandwidth report'
		} finally {
			loading.value = false
		}
	}

	const getMemberReport = async (vhostId: string, filters: ReportFilters = {}): Promise<void> => {
		loading.value = true
		error.value = null

		const query = `
		query GetUserSummary($vhost: Int!, $startDate: BigInt!, $endDate: BigInt!) {
			Vhost {
				getUserSummary(vhost: $vhost, startDate: $startDate, endDate: $endDate) {
					ActiveUsers
					RegisteredUsers
					NewUsers
					CountMale
					CountFemale
					CountUnknown
					CountOptin
				}
			}
		}
		`

		const now = Math.floor(Date.now() / 1000)
		const startDate = filters.startDate
			? Math.floor(new Date(filters.startDate).getTime() / 1000)
			: now - (365 * 24 * 60 * 60)

		try {
			const data = await sendGraphQLQuery(query, {
				vhost: +vhostId,
				startDate,
				endDate: now,
			})

			if (data?.Vhost?.getUserSummary) {
				const summary = data.Vhost.getUserSummary
				memberData.value = [
					{ date: 'Active', value: summary.ActiveUsers, label: 'Active Users' },
					{ date: 'Registered', value: summary.RegisteredUsers, label: 'Registered' },
					{ date: 'New', value: summary.NewUsers, label: 'New Users' },
					{ date: 'Opted In', value: summary.CountOptin, label: 'Opted In' },
				]
			}
		} catch (err: any) {
			console.error('Error fetching member report:', err)
			error.value = err.message || 'Failed to fetch member report'
		} finally {
			loading.value = false
		}
	}

	return {
		uploadData,
		viewData,
		bandwidthData,
		memberData,
		loading,
		error,
		getUploadReport,
		getViewReport,
		getBandwidthReport,
		getMemberReport,
	}
}
